import type { JSX, ReactNode } from 'react';

import { cn } from '@/lib/cn';

type ChipTone = 'neutral' | 'accent';

interface ChipProps {
  children: ReactNode;
  tone?: ChipTone;
  className?: string;
}

const TONES: Record<ChipTone, string> = {
  neutral: 'border-line bg-surface-muted text-subtle',
  accent: 'border-transparent bg-accent-soft text-accent-strong',
};

/** Compact label for technologies, sectors and focus areas. */
export function Chip({ children, tone = 'neutral', className }: ChipProps): JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm border px-2.5 py-1 text-xs font-medium',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

interface ChipListProps {
  items: readonly string[];
  tone?: ChipTone;
  label: string;
  className?: string;
}

/**
 * Technology lists are real lists: exposing them as `ul`/`li` lets screen
 * readers announce the item count instead of a run-on string.
 */
export function ChipList({
  items,
  tone = 'neutral',
  label,
  className,
}: ChipListProps): JSX.Element {
  return (
    <ul aria-label={label} className={cn('flex flex-wrap gap-2', className)}>
      {items.map((item) => (
        <li key={item}>
          <Chip tone={tone}>{item}</Chip>
        </li>
      ))}
    </ul>
  );
}
